import {
  Building2,
  Fence,
  Hammer,
  House,
  PaintRoller,
  Snowflake,
  Trees,
  Wrench,
  Zap,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'


/**
 * THE SERVICE TAXONOMY.
 * ============================================================================
 * One tree, and the only place service names, slugs, copy, imagery and
 * hierarchy are declared. Every service control on the site reads from here:
 * the homepage section, the browse page, the listing form's category select,
 * the search filters, and the icon a contractor card falls back to.
 *
 * ── WHY IT IS A TREE ───────────────────────────────────────────────────────
 * The eventual taxonomy is granular — "Bathroom", "Kitchen Sinks" — and a flat
 * list cannot express that a specific service belongs to a category without
 * encoding the relationship in a component. `children` is recursive, so
 * Category → Subcategory → Specific service works today with no change to any
 * page that renders it.
 *
 * ── WHY IT IS SHORT ────────────────────────────────────────────────────────
 * The real list has not been supplied. So: eight categories, all of which
 * already existed in the project, and child services taken ONLY from names
 * that already appear in the demo contractor records. Nothing here is
 * invented to fill the tree out — a guessed taxonomy committed as product
 * data is worse than a small true one, because everything downstream starts
 * depending on the guesses.
 *
 * Three categories deliberately have no children, because no child data
 * exists for them. That is a supported state, not a gap to be papered over.
 *
 * ── HOW IT GROWS ───────────────────────────────────────────────────────────
 * Adding services is a change to THIS FILE. No page, card, filter or select
 * needs touching — they all render from the tree and count what they find.
 */

/**
 * A node anywhere in the tree.
 *
 * `id` is stable and independent of the slug, so a display name or URL can be
 * corrected later without breaking references held elsewhere.
 */
export interface ServiceNode {
  id: string
  name: string
  /** URL segment. Public and permanent once minted. */
  slug: string
  /** One short line. Omitted rather than padded when unknown. */
  description?: string
  /**
   * Alternative words people search for. NOT a synonym engine — a handful of
   * genuinely common phrasings ("air conditioning" for HVAC) so the search box
   * does not fail on the word a homeowner actually uses.
   */
  aliases?: readonly string[]
  /** Sub-services. Recursive, so a third level needs no new type. */
  children?: readonly ServiceNode[]
}

/**
 * A top-level category. Carries the presentation a category needs and a leaf
 * does not: an icon for every context, and a photograph for the ones with
 * room for it.
 */
export interface ServiceCategory extends ServiceNode {
  description: string
  icon: LucideIcon
  /** Editorial selection, not a popularity measurement. */
  featured?: boolean
}

/** Shorthand for a leaf whose only data is its name. */
const leaf = (parent: string, name: string): ServiceNode => ({
  id: `${parent}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
  name,
  slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
})

export const SERVICE_CATEGORIES: readonly ServiceCategory[] = [
  {
    id: 'roofing',
    name: 'Roofing',
    slug: 'roofing',
    description: 'Repair, replacement & installation',
    icon: House,
    featured: true,
    aliases: ['roof', 'roofer', 'shingles', 'gutters'],
    children: [
      leaf('roofing', 'Roof Repair'),
      leaf('roofing', 'Roof Replacement'),
      leaf('roofing', 'New Roof Installation'),
      leaf('roofing', 'Roof Inspection'),
      leaf('roofing', 'Storm Damage'),
      leaf('roofing', 'Gutter Installation'),
      leaf('roofing', 'Metal Roofing'),
      leaf('roofing', 'Flat Roofing'),
      leaf('roofing', 'Soffit & Fascia'),
    ],
  },
  {
    id: 'plumbing',
    name: 'Plumbing',
    slug: 'plumbing',
    description: 'Repairs, installations & drainage',
    icon: Wrench,
    featured: true,
    aliases: ['plumber', 'drains', 'pipes', 'water heater'],
    children: [
      leaf('plumbing', 'Leak Repair'),
      leaf('plumbing', 'Drain Cleaning'),
      leaf('plumbing', 'Water Heaters'),
      leaf('plumbing', 'Fixture Installation'),
    ],
  },
  {
    id: 'hvac',
    name: 'HVAC',
    slug: 'hvac',
    description: 'Heating, cooling & ventilation',
    icon: Snowflake,
    featured: true,
    aliases: ['air conditioning', 'ac', 'furnace', 'heating', 'cooling', 'heat pump'],
    children: [
      leaf('hvac', 'AC Repair'),
      leaf('hvac', 'Heating Repair'),
      leaf('hvac', 'System Replacement'),
    ],
  },
  {
    id: 'electrical',
    name: 'Electrical',
    slug: 'electrical',
    description: 'Wiring, lighting & repairs',
    icon: Zap,
    featured: true,
    aliases: ['electrician', 'wiring', 'outlets', 'breaker'],
    children: [
      leaf('electrical', 'Panel Upgrades'),
      leaf('electrical', 'Rewiring'),
      leaf('electrical', 'Lighting'),
      leaf('electrical', 'EV Chargers'),
    ],
  },
  {
    id: 'painting',
    name: 'Painting',
    slug: 'painting',
    description: 'Interior & exterior painting',
    icon: PaintRoller,
    featured: true,
    aliases: ['painter', 'paint', 'staining'],
    // No child data exists yet. Rendering a category alone is a supported
    // state, not something to fill with guesses.
  },
  {
    id: 'remodeling',
    name: 'Remodeling',
    slug: 'remodeling',
    description: 'Renovations, upgrades & repairs',
    icon: Hammer,
    featured: true,
    /**
     * Deliberately NOT "kitchen" or "bathroom". Both are already child names,
     * and an alias that duplicates a child makes the category self-match —
     * which shows every sibling service instead of narrowing to the one
     * searched for. Someone typing "bathroom" should get Bathroom Remodeling,
     * not Windows.
     */
    aliases: ['renovation', 'remodel'],
    children: [
      leaf('remodeling', 'Kitchen Remodeling'),
      leaf('remodeling', 'Bathroom Remodeling'),
      leaf('remodeling', 'Whole-home Renovation'),
      leaf('remodeling', 'Windows'),
    ],
  },
  {
    id: 'landscaping',
    name: 'Landscaping',
    slug: 'landscaping',
    description: 'Lawns, gardens & outdoor spaces',
    icon: Trees,
    featured: true,
    aliases: ['lawn', 'yard', 'garden', 'landscaper'],
  },
  {
    id: 'fencing',
    name: 'Fencing',
    slug: 'fencing',
    description: 'Installation, repair & replacement',
    icon: Fence,
    featured: true,
    aliases: ['fence', 'gate'],
  },
]

/** The neutral mark for a slug the taxonomy does not know. */
export const FALLBACK_SERVICE_ICON: LucideIcon = Building2

/** Every node in the tree, flattened. Depth-agnostic, so a third level works. */
export function flattenServices(
  nodes: readonly ServiceNode[] = SERVICE_CATEGORIES,
): ServiceNode[] {
  return nodes.flatMap((node) => [node, ...flattenServices(node.children ?? [])])
}

/**
 * Does this node match a search term?
 *
 * Name, description and aliases — and a CATEGORY also matches on its
 * children, so typing "bathroom" surfaces Remodeling rather than nothing.
 */
export function serviceMatches(node: ServiceNode, term: string): boolean {
  const q = term.trim().toLowerCase()
  if (!q) return true

  const haystack = [node.name, node.description ?? '', ...(node.aliases ?? [])]
  if (haystack.some((value) => value.toLowerCase().includes(q))) return true

  return (node.children ?? []).some((child) => serviceMatches(child, q))
}

/** The child services matching a term, for highlighting within a category. */
export function matchingChildren(
  category: ServiceNode,
  term: string,
): readonly ServiceNode[] {
  const children = category.children ?? []
  const q = term.trim().toLowerCase()
  if (!q) return children
  // A category matched by its own name keeps all of its children; one matched
  // only through a child shows just the children that matched.
  const selfMatch = [category.name, category.description ?? '', ...(category.aliases ?? [])].some(
    (value) => value.toLowerCase().includes(q),
  )
  return selfMatch ? children : children.filter((child) => serviceMatches(child, q))
}
